<template>
    <div class="ui-page">
        <ui-page-header title="Dados bancários" subtitle="Contas informadas pelos credores para recebimento dos pagamentos.">
            <v-btn color="primary" depressed :loading="exporting" @click="exportCsv">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                Exportar planilha
            </v-btn>
        </ui-page-header>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="filters.name" placeholder="Buscar pelo credor" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-autocomplete v-model="filters.client_id" :items="clientFilterOptions" item-text="name" item-value="id"
                    placeholder="Todos os casos" clearable dense outlined hide-details no-data-text="Nenhum caso encontrado"
                    class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="applyFilters"></v-autocomplete>
                <v-text-field v-model="filters.registration" placeholder="CPF/CNPJ" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field v-model="filters.bank_name" placeholder="Banco" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
            </template>

            <v-data-table :headers="headers" :items="items" :server-items-length="total" :loading="loading"
                :options.sync="options" disable-sort hide-default-footer class="ui-clickable-rows" @click:row="openEdit"
                no-data-text="Nenhum dado bancário encontrado" loading-text="Carregando dados...">
                <template v-slot:[`item.user.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.user ? item.user.name : 'Credor não encontrado' }}</div>
                    <div class="ui-cell-sub">{{ item.user ? item.user.registration : '' }}</div>
                </template>
                <template v-slot:[`item.client.name`]="{ item }">
                    <span v-if="item.client">{{ item.client.name }}</span>
                    <ui-badge v-else text="Sem caso vinculado" color="warning" />
                </template>
                <template v-slot:[`item.bank`]="{ item }">
                    {{ item.bank_code }} - {{ item.bank_name }}
                </template>
                <template v-slot:[`item.account`]="{ item }">
                    Ag. {{ item.agency }} · Conta {{ item.account_number }}{{ item.account_digit ? '-' + item.account_digit : '' }}
                </template>
                <template v-slot:[`item.actions`]>
                    <v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon>
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="total" :page="options.page" :items-per-page="options.itemsPerPage"
                    :disabled="loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <v-dialog v-model="dialog" max-width="900px" persistent>
            <v-card>
                <v-card-title>Editar dados bancários</v-card-title>
                <v-card-text>
                    <v-form ref="editForm">
                        <v-row>
                            <v-col cols="12">
                                <v-autocomplete v-model="form.client_id" :items="clients" item-text="name" item-value="id"
                                    label="Caso" clearable hint="Deixe vazio para manter como Sem caso vinculado" persistent-hint dense outlined></v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6"><v-text-field v-model="form.bank_name" label="Nome do banco" :rules="[$rules.required]" dense outlined></v-text-field></v-col>
                            <v-col cols="12" md="6"><v-text-field v-model="form.bank_code" label="Número do banco" :rules="[$rules.required]" dense outlined></v-text-field></v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="4"><v-text-field v-model="form.agency" label="Agência" :rules="[$rules.required]" dense outlined></v-text-field></v-col>
                            <v-col cols="12" md="5"><v-text-field v-model="form.account_number" label="Número da conta" :rules="[$rules.required]" dense outlined></v-text-field></v-col>
                            <v-col cols="12" md="3"><v-text-field v-model="form.account_digit" label="Dígito" dense outlined></v-text-field></v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-select v-model="form.account_type" :items="accountTypes" item-text="name" item-value="id"
                                    label="Tipo de conta" :rules="[$rules.required]" dense outlined></v-select>
                            </v-col>
                            <v-col cols="12" md="6"><v-text-field v-model="form.registration" label="CPF/CNPJ da conta" :rules="[$rules.required]" dense outlined></v-text-field></v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-select v-model="form.pix_type" :items="pixTypes" item-text="name" item-value="id" clearable label="Tipo de chave PIX" dense outlined></v-select>
                            </v-col>
                            <v-col cols="12" md="6"><v-text-field v-model="form.pix_key" label="Chave PIX" dense outlined></v-text-field></v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-radio-group v-model="accountOwnership" row @change="handleOwnershipChange">
                                    <v-radio label="Conta própria" value="own"></v-radio>
                                    <v-radio label="Conta de procurador" value="lawyer"></v-radio>
                                </v-radio-group>
                            </v-col>
                            <v-col v-if="accountOwnership === 'lawyer'" cols="12">
                                <v-autocomplete v-model="form.lawyer_id" :items="lawyers" item-text="name" item-value="id"
                                    label="Procurador" :rules="[$rules.required]" dense outlined></v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn text @click="dialog = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" :loading="saving" @click="save">Salvar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { downloadBlob } from "@/common/util/export-xlsx.util";
import axios from 'axios'
import _ from 'lodash'

export default {
    name: 'BankInformationList',
    data: () => ({
        breadcrumbs: [
            { text: 'Homepage', to: '/home', exact: true },
            { text: 'Dados bancários', disabled: true },
        ],
        clients: [],
        lawyers: [],
        items: [],
        total: 0,
        loading: false,
        exporting: false,
        saving: false,
        dialog: false,
        form: {},
        accountOwnership: 'own',
        filters: { client_id: null, name: '', registration: '', bank_name: '' },
        options: { page: 1, itemsPerPage: 10 },
        footerOptions: { itemsPerPageText: 'Itens por página:', itemsPerPageOptions: [10, 20, 30, 50], pageText: '{0}-{1} de {2}' },
        headers: [
            { text: 'Credor', value: 'user.name' },
            { text: 'Caso', value: 'client.name' },
            { text: 'Banco', value: 'bank' },
            { text: 'Conta', value: 'account' },
            { text: 'Chave PIX', value: 'pix_key' },
            { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
        ],
        accountTypes: [{ id: 1, name: 'Conta corrente' }, { id: 2, name: 'Conta poupança' }],
        pixTypes: [{ id: 1, name: 'CPF' }, { id: 2, name: 'CNPJ' }, { id: 3, name: 'Celular' }, { id: 4, name: 'E-mail' }, { id: 5, name: 'Chave aleatória' }],
    }),
    created() {
        this.loadDependencies()
        this.load()
    },
    computed: {
        hasFilters() {
            return Object.values(this.filters).some((value) => value)
        },
        clientFilterOptions() {
            return [
                { id: 'without_case', name: 'Sem caso vinculado' },
                ...this.clients,
            ]
        },
    },
    methods: {
        applyFilters() {
            this.options.page = 1
            this.load()
        },
        filterDebounced: _.debounce(function () {
            this.applyFilters()
        }, 400),
        clearFilters() {
            this.filters = { client_id: null, name: '', registration: '', bank_name: '' }
            this.applyFilters()
        },
        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.options.itemsPerPage = size
        },
        pageChanged(page) {
            this.options.page = page
            this.load()
        },
        searchParams() {
            const search = Object.fromEntries(Object.entries(this.filters).filter(([, value]) => value !== '' && value !== null))
            return { search: JSON.stringify(search) }
        },
        async loadDependencies() {
            try {
                const [clientsResponse, lawyersResponse] = await Promise.all([
                    axios.get('/api/clients', { params: { page: 1, limit: 100000 } }),
                    axios.get('/api/admin/lawyer', { params: { page: 1, limit: 100000 } }),
                ])
                this.clients = (clientsResponse.data.content.data || []).sort((a, b) => a.name.localeCompare(b.name))
                this.lawyers = lawyersResponse.data.content.data || []
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 })
            }
        },
        async load(resetPage = false) {
            if (resetPage === true) this.options.page = 1
            this.loading = true
            try {
                const response = await axios.get('/api/admin/bank-informations', {
                    params: { ...this.searchParams(), page: this.options.page, limit: this.options.itemsPerPage }
                })
                this.items = response.data.content.data
                this.total = response.data.content.paging.total
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 })
            } finally {
                this.loading = false
            }
        },
        openEdit(item) {
            this.form = { ...item }
            this.accountOwnership = item.lawyer_id ? 'lawyer' : 'own'
            this.dialog = true
        },
        handleOwnershipChange(value) {
            if (value === 'own') this.form.lawyer_id = null
        },
        async save() {
            if (!this.$refs.editForm.validate()) return
            this.saving = true
            try {
                await axios.patch(`/api/admin/bank-informations/${this.form.id}`, this.form)
                this.$dialog.notify.info('Dados bancários atualizados com sucesso.', { position: 'top-right', timeout: 2500 })
                this.dialog = false
                await this.load()
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 })
            } finally {
                this.saving = false
            }
        },
        async exportCsv() {
            this.exporting = true
            try {
                const response = await axios.get('/api/admin/bank-informations-export/csv', {
                    params: this.searchParams(), responseType: 'blob'
                })
                downloadBlob(response.data, this.filters.client_id === 'without_case'
                    ? 'dados_bancarios_sem_caso'
                    : (this.filters.client_id ? `dados_bancarios_caso_${this.filters.client_id}` : 'dados_bancarios'))
            } catch (error) {
                this.$dialog.notify.error('Não foi possível exportar os dados bancários.', { position: 'top-right', timeout: 3000 })
            } finally {
                this.exporting = false
            }
        },
    },
}
</script>
